import EditIcon from "@mui/icons-material/Edit";
import CopyIcon from "@mui/icons-material/PersonSearch";
import { Alert, AlertTitle, Box, Button, Stack } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCase } from "../../context/CaseContext";
import { useTheme } from "../../context/ThemeContext";
import { Stage } from "../../shared/CaseConfiguration";
import { CostCopyDialog } from "./CostCopyDialog";
import {
  EditableCostsPerStage,
  EditableCostsPerStageTable,
} from "./EditableCostsPerStageTable";

export const CostsEditor = ({
  costs,
  partyId,
  leftAt,
  updateCosts,
  getCosts,
}: {
  costs: EditableCostsPerStage[];
  partyId: string;
  // the stage at which the party left the case: none of its costs can change
  leftAt?: Stage;
  updateCosts: (update: typeof costs) => Promise<void>;
  getCosts?: (partyId: string) => EditableCostsPerStage[] | undefined;
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.budget.table",
  });
  const [editedCosts, setEditedCosts] = useState<EditableCostsPerStage[]>();
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [costTimestamp, setCostTimestamp] = useState(Date.now());

  const save = () => {
    const toSave = editedCosts;
    // is there anything to save?
    if (toSave === undefined) return;
    setSaving(true);
    updateCosts(toSave)
      .then(() => {
        setEditedCosts(undefined);
        setSaving(false);
        setError(undefined);
      })
      .catch((error) => {
        setEditedCosts(undefined);
        setSaving(false);
        console.warn("Failed to save costs", error);
        setError(error);
      });
  };

  const caseContext = useCase();
  const parties =
    caseContext
      .getCase()
      .parties?.sort((p1, p2) => p1.name.localeCompare(p2.name)) || [];

  const editing = editedCosts !== undefined;
  const busy = saving || copyDialogOpen;
  const buttonSx = { textTransform: "none", fontWeight: 600 } as const;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mx: 2.5, mt: 2 }}>
          <AlertTitle>{t("error.save")}</AlertTitle>
          {error}
        </Alert>
      )}
      <EditableCostsPerStageTable
        key={"costs_" + costTimestamp}
        costs={editedCosts !== undefined ? editedCosts : costs}
        leftAt={leftAt}
        editable={editing && !saving}
        updateCosts={(newCosts) => {
          setEditedCosts(newCosts);
        }}
      />
      {/* The table shows the draft while it is edited: here it is stored or dropped */}
      <Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        sx={{
          alignItems: "center",
          justifyContent: editing ? "space-between" : "flex-end",
          flexWrap: "wrap",
          px: 2.5,
          py: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        {editing && getCosts && (
          <Button
            disabled={busy}
            variant="outlined"
            startIcon={<CopyIcon />}
            onClick={() => setCopyDialogOpen(true)}
            sx={buttonSx}
          >
            {t("copy")}
          </Button>
        )}
        {editing ? (
          <Stack direction="row" spacing={1.5}>
            <Button
              color="inherit"
              disabled={busy}
              onClick={() => setEditedCosts(undefined)}
              sx={buttonSx}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              disabled={busy}
              loading={saving}
              onClick={save}
              sx={buttonSx}
            >
              {t("save")}
            </Button>
          </Stack>
        ) : (
          <Button
            // the draft, and everything done with it, starts here
            disabled={busy || caseContext.readOnly || leftAt !== undefined}
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => setEditedCosts(costs)}
            sx={buttonSx}
          >
            {t("edit")}
          </Button>
        )}
      </Stack>
      <CostCopyDialog
        open={copyDialogOpen}
        onClose={(copy) => {
          setCopyDialogOpen(false);
          if (copy === undefined || getCosts === undefined) return;
          const newCosts = getCosts(copy.sourcePartyId);
          if (newCosts === undefined)
            console.warn("Got no costs for party " + copy.sourcePartyId);
          console.debug(
            "Replacing costs of '" +
              partyId +
              "' with costs of '" +
              copy.sourcePartyId +
              "'",
          );
          setEditedCosts(newCosts);
          // needed so the edit table is rerendered with new costs (cells hold each cost in their state during editing)
          setCostTimestamp(Date.now());
        }}
        sources={parties.filter((party) => party.id !== partyId)}
        target={parties.find((party) => party.id === partyId)?.name || partyId}
      />
    </Box>
  );
};
